"use client";

import { useQuery } from "@urql/next";
import { graphql } from "gql.tada";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
} from "react";
import { useAiChatUiContext } from "../../ai-chat-ui-context";
import type { AddToolApprovalResponse } from "./types";

type RunApprovalSubmission = (
  approvalKey: string,
  submission: () => Promise<void>,
) => Promise<boolean>;

const ApprovalSubmissionContext = createContext<RunApprovalSubmission | null>(
  null,
);

/**
 * 同一tool callのapprove/denyを共有Setで排他し、完了・失敗のどちらでもkeyを解放します。
 * 同じapproval UI群を描画する上位に一度だけmountしてください。
 */
export function ApprovalSubmissionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const inFlightApprovalKeysRef = useRef(new Set<string>());
  const runApprovalSubmission = useCallback<RunApprovalSubmission>(
    async (approvalKey, submission) => {
      if (inFlightApprovalKeysRef.current.has(approvalKey)) {
        return false;
      }

      inFlightApprovalKeysRef.current.add(approvalKey);
      try {
        await submission();
        return true;
      } finally {
        inFlightApprovalKeysRef.current.delete(approvalKey);
      }
    },
    [],
  );

  return (
    <ApprovalSubmissionContext.Provider value={runApprovalSubmission}>
      {children}
    </ApprovalSubmissionContext.Provider>
  );
}

/**
 * Provider配下では共有guardを返し、単独利用時はcomponent-local guardへfallbackします。
 * submissionの失敗は呼び出し元へ伝播し、`finally`で再試行可能な状態へ戻します。
 */
export function useApprovalSubmissionGuard(): RunApprovalSubmission {
  const sharedGuard = useContext(ApprovalSubmissionContext);
  const localInFlightApprovalKeysRef = useRef(new Set<string>());
  const localGuard = useCallback<RunApprovalSubmission>(
    async (approvalKey, submission) => {
      if (localInFlightApprovalKeysRef.current.has(approvalKey)) {
        return false;
      }

      localInFlightApprovalKeysRef.current.add(approvalKey);
      try {
        await submission();
        return true;
      } finally {
        localInFlightApprovalKeysRef.current.delete(approvalKey);
      }
    },
    [],
  );

  return sharedGuard ?? localGuard;
}

type NativeToolApproval = {
  approvalId: string;
  addToolApprovalResponse: AddToolApprovalResponse;
  canRespond: boolean;
  responseDisabledReason:
    | "checking"
    | "error"
    | "forbidden"
    | "missing-chat"
    | null;
  responseDisabledReasonId: string;
  retryPermissionCheck: () => void;
};

const NativeToolApprovalContext = createContext<NativeToolApproval | null>(
  null,
);

/**
 * native approvalへ未移行のconfirmation toolについて、従来どおりchat作成者だけに応答を許可します。
 * native approval利用時はqueryを停止し、provider側のpermission判定だけを使用します。
 */
export function useLegacyToolApprovalPermission(enabled: boolean): boolean {
  const { chatId } = useAiChatUiContext();
  const hasPersistedChat = Boolean(chatId);
  const [{ data }] = useQuery({
    query: graphql(`
      query LegacyToolApprovalPermission($chatId: ID!) {
        viewer {
          id
          __typename
        }
        node(id: $chatId) {
          id
          __typename
          ... on AgentChat {
            createdByUserID
          }
        }
      }
    `),
    variables: {
      chatId: typeof chatId === "string" ? decodeURIComponent(chatId) : "",
    },
    pause: !enabled || !chatId,
  });
  const chatCreatedByUserId =
    data?.node?.__typename === "AgentChat" ? data.node.createdByUserID : null;

  if (!enabled) {
    return false;
  }

  // draft 保存時は旧送信 queue を破棄するため、その最中に legacy confirmation を
  // 応答すると continuation まで失効する。chatId と作成者確認が揃うまで閉じる。
  return Boolean(
    hasPersistedChat &&
      data?.viewer?.id &&
      data.viewer.id === chatCreatedByUserId,
  );
}

/**
 * native approval の応答 handler と chat 作成者権限を配下の操作 UI へ提供する。
 * 権限確認中・取得失敗・第三者閲覧を別状態として公開し、button が fail-closed になるようにする。
 */
export function NativeToolApprovalProvider({
  approvalId,
  addToolApprovalResponse,
  children,
}: Pick<NativeToolApproval, "approvalId" | "addToolApprovalResponse"> & {
  children: ReactNode;
}) {
  const { chatId } = useAiChatUiContext();
  const hasPersistedChat = Boolean(chatId);
  const [{ data, error, fetching }, reexecutePermissionQuery] = useQuery({
    query: graphql(`
      query NativeToolApprovalPermission($chatId: ID!) {
        viewer {
          id
          __typename
        }
        node(id: $chatId) {
          id
          __typename
          ... on AgentChat {
            createdByUserID
          }
        }
      }
    `),
    variables: {
      chatId: typeof chatId === "string" ? decodeURIComponent(chatId) : "",
    },
    pause: !chatId,
  });
  const chatCreatedByUserId =
    data?.node?.__typename === "AgentChat" ? data.node.createdByUserID : null;
  const canRespond = Boolean(
    hasPersistedChat &&
      !error &&
      !fetching &&
      data?.viewer?.id &&
      data.viewer.id === chatCreatedByUserId,
  );
  const responseDisabledReason: NativeToolApproval["responseDisabledReason"] =
    canRespond
      ? null
      : !hasPersistedChat
        ? "missing-chat"
        : fetching
          ? "checking"
          : error || !data
            ? "error"
            : "forbidden";
  const responseDisabledReasonId = `tool-approval-${approvalId}-permission`;
  const retryPermissionCheck = useCallback(() => {
    reexecutePermissionQuery({ requestPolicy: "network-only" });
  }, [reexecutePermissionQuery]);

  return (
    <NativeToolApprovalContext.Provider
      value={{
        approvalId,
        addToolApprovalResponse,
        canRespond,
        responseDisabledReason,
        responseDisabledReasonId,
        retryPermissionCheck,
      }}
    >
      {children}
    </NativeToolApprovalContext.Provider>
  );
}

/** native approval 配下なら応答契約を返し、legacy confirmation では `null` を返す。 */
export function useNativeToolApproval(): NativeToolApproval | null {
  return useContext(NativeToolApprovalContext);
}
